import React from 'react';
import { Provider } from 'react-redux';
import { ApolloProvider, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import JssProvider from 'react-jss/lib/JssProvider';
import { createGenerateClassName, jssPreset } from '@material-ui/core/styles';
import { create } from 'jss';
import { SnackbarProvider } from 'notistack';
import CssBaseline from '@material-ui/core/CssBaseline';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import client from '../../lib/apolloClient';
import configureStore from '../../store/findBobPublicStore';
import themes from '../../lib/themes';
import MultiLineValuation from '../../components/Succession/MultiLineValuation';
import SuccessionThemeProvider from '../../components/Succession/SuccessionThemeProvider';
import SingleColumnTemplate from '../../../../ui/layouts/SingleColumnTemplate';
import { ArrowLeftIcon, UserCircleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const jss = create({
  ...jssPreset(),
  insertionPoint: document.getElementById('jss-insertion-point'),
});
const generateClassName = createGenerateClassName();

const store = configureStore();

// Query to fetch participant valuation data including the stored calculation result
const PARTICIPANT_VALUATION_QUERY = gql`
  query ParticipantValuation($enrollment_id: ID!) {
    participant_valuation(enrollment_id: $enrollment_id) {
      enrollment_id
      participant_name
      participant_id
      line_inputs
      ebitda_adjustments
      has_saved_valuation
      validation_status
      program_title
      scenario_name
      valuation_result
      gross_production
      company {
        id
        feature_types
      }
    }
  }
`;

// Note: We use the stored valuation_result from the scenario instead of re-calculating.
// This ensures the coach sees exactly what the participant saved.

// Inner component that uses hooks
const ParticipantValuationInner = ({ enrollmentId }) => {
  // Fetch participant valuation data including the stored calculation result
  const { data: participantData, loading, error } = useQuery(
    PARTICIPANT_VALUATION_QUERY,
    {
      variables: { enrollment_id: enrollmentId },
      skip: !enrollmentId,
    }
  );

  const participantValuation = participantData?.participant_valuation;
  const lineInputs = participantValuation?.line_inputs || {};
  // Use the stored valuation result directly - this is the exact calculation the participant saved
  const valuationResult = participantValuation?.valuation_result;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4" />
          <p className="text-succession-text-secondary">Loading participant valuation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center text-red-600">
          <p>Error loading valuation: {error.message}</p>
        </div>
      </div>
    );
  }

  if (!participantValuation) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-succession-text-secondary">Participant valuation not found or not authorized.</p>
          <a
            href="/coach/portal"
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg border border-succession-border hover:bg-succession-bg-secondary transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Coach Portal
          </a>
        </div>
      </div>
    );
  }

  if (!participantValuation.has_saved_valuation) {
    return (
      <SuccessionThemeProvider>
        <SingleColumnTemplate
          title={`${participantValuation.participant_name}'s Valuation`}
          subtitle={participantValuation.program_title}
        >
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center p-8 rounded-lg bg-amber-50 border border-amber-200 max-w-md">
              <UserCircleIcon className="w-12 h-12 mx-auto mb-4 text-amber-500" />
              <h3 className="text-lg font-semibold text-amber-800 mb-2">No Saved Valuation</h3>
              <p className="text-amber-700 mb-4">
                {participantValuation.participant_name} has not yet saved their valuation.
                They need to complete and save their valuation before you can review it.
              </p>
              <a
                href="/coach/portal"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to Coach Portal
              </a>
            </div>
          </div>
        </SingleColumnTemplate>
      </SuccessionThemeProvider>
    );
  }

  return (
    <SuccessionThemeProvider>
      {/* Header with back link and participant info */}
      <div className="bg-succession-bg-primary border-b border-succession-border px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a
                href="/coach/portal"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-succession-text-secondary hover:bg-succession-bg-secondary transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to Portal
              </a>
              <div className="h-6 w-px bg-succession-border" />
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                  style={{ backgroundColor: '#10B981' }}
                >
                  {(participantValuation.participant_name || 'P')[0].toUpperCase()}
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-succession-text-primary">
                    {participantValuation.participant_name}'s Valuation
                    {participantValuation.scenario_name && (
                      <span className="text-base font-normal text-succession-text-secondary ml-2">
                        ({participantValuation.scenario_name})
                      </span>
                    )}
                  </h1>
                  <p className="text-sm text-succession-text-secondary">
                    {participantValuation.program_title} • Read-only View
                  </p>
                </div>
              </div>
            </div>
            
            {/* Validation status badge */}
            {participantValuation.validation_status === 'validated' && (
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white"
                style={{
                  background: 'linear-gradient(to right, #10B981, #14B8A6)',
                }}
              >
                <ShieldCheckIcon className="w-4 h-4" />
                Validated
              </span>
            )}
          </div>
        </div>
      </div>

      <MultiLineValuation
        loading={false}
        error={null}
        result={valuationResult}
        scenarios={[]}
        initialInputs={lineInputs}
        initialExpenses={participantValuation.ebitda_adjustments}
        allowManualGrowth={true}
        currentUser={{}} // Coach is viewing, not the participant
        viewerIsPrivileged={true}
        // The participant is the subject of the valuation, so their company (for the
        // DCF range flag) and gross production (the 1x T12 floor) travel on viewerUser.
        viewerUser={{
          name: participantValuation.participant_name,
          company: participantValuation.company,
          gross_production: participantValuation.gross_production,
        }}
        validationData={null}
        loadedScenarioId={null}
        loadedScenarioName={participantValuation.scenario_name}
        saving={false}
        updating={false}
        readOnly={true} // Read-only mode for coach viewing
        showInputDrawer={true} // Show the input drawer in read-only mode
        onRecalculate={() => {}} // No-op for read-only
        onSave={() => Promise.resolve({ success: false, error: 'Read-only mode' })}
        onUpdate={() => Promise.resolve({ success: false, error: 'Read-only mode' })}
        onLoadScenario={() => {}}
        onClearScenario={() => {}}
      />
    </SuccessionThemeProvider>
  );
};

// Main container component
const ParticipantValuationContainer = (props) => {
  const { enrollment_id: enrollmentId } = props;

  return (
    <JssProvider jss={jss} generateClassName={generateClassName}>
      <MuiThemeProvider theme={createMuiTheme(themes.findbobTheme)}>
        <Provider store={store}>
          <SnackbarProvider maxSnack={3}>
            <CssBaseline />
            <ApolloProvider client={client}>
              <ParticipantValuationInner enrollmentId={enrollmentId} />
            </ApolloProvider>
          </SnackbarProvider>
        </Provider>
      </MuiThemeProvider>
    </JssProvider>
  );
};

export default ParticipantValuationContainer;
